import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CheckCircle, MapPin, Package, Truck, User, Home, ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConfirmationPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [pickupSlot, setPickupSlot] = useState(null);
  const [deliverySlot, setDeliverySlot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await axios.get(`${API}/bookings/${bookingId}`);
        setBooking(response.data);
        
        // Fetch slot details
        const slotsResponse = await axios.get(`${API}/slots/${response.data.zone_id}`);
        const slots = slotsResponse.data;
        
        // For new bookings with pickup/delivery
        if (response.data.pickup_slot_id) {
          setPickupSlot(slots.find(s => s.id === response.data.pickup_slot_id));
          setDeliverySlot(slots.find(s => s.id === response.data.delivery_slot_id));
        } else {
          // Legacy booking with single slot
          setPickupSlot(slots.find(s => s.id === response.data.slot_id));
        }
      } catch (error) {
        console.error("Error fetching booking:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBooking();
  }, [bookingId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Réservation introuvable</h1>
        <Button onClick={() => navigate("/")} className="btn-primary">
          Retour à l'accueil
        </Button>
      </div>
    );
  }

  const zoneName = booking.zone_id === "lyon" ? "Lyon" : "Saint-Rambert-d'Albon";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Success Message */}
        <div className="text-center mb-12 fade-in">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Réservation confirmée !
          </h1>
          <p className="text-slate-600">
            Votre réservation a été enregistrée avec succès.
          </p>
        </div>

        {/* Booking Details */}
        <Card className="fade-in">
          <CardContent className="p-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Détails de votre réservation
            </h2>

            <div className="space-y-6">
              {/* Zone */}
              <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Zone de service</p>
                  <p className="font-medium text-slate-900">{zoneName}</p>
                </div>
              </div>

              {/* Pickup */}
              {pickupSlot && (
                <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Ramassage</p>
                    <p className="font-medium text-slate-900">
                      {format(new Date(pickupSlot.date), "EEEE d MMMM yyyy", { locale: fr })}
                    </p>
                    <p className="text-slate-600">
                      {pickupSlot.start_time} - {pickupSlot.end_time}
                    </p>
                  </div>
                </div>
              )}

              {/* Delivery */}
              {deliverySlot && (
                <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Truck className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Livraison</p>
                    <p className="font-medium text-slate-900">
                      {format(new Date(deliverySlot.date), "EEEE d MMMM yyyy", { locale: fr })}
                    </p>
                    <p className="text-slate-600">
                      {deliverySlot.start_time} - {deliverySlot.end_time}
                    </p>
                  </div>
                </div>
              )}

              {/* Surcharge Warning */}
              {booking.has_surcharge && (
                <div className="flex items-start gap-4 pb-6 border-b border-slate-100 bg-amber-50 p-4 rounded-lg -mx-4">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">Supplément express applicable</p>
                    <p className="text-sm text-amber-700">+{booking.surcharge_percent || 50}% sur la facture ({booking.surcharge_percent === 50 ? "24h" : "48h"})</p>
                  </div>
                </div>
              )}

              {/* Client Info */}
              <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium text-slate-900">
                    {booking.first_name} {booking.last_name}
                  </p>
                  {booking.phone && <p className="text-slate-600">{booking.phone}</p>}
                  {booking.email && <p className="text-slate-600">{booking.email}</p>}
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Home className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Adresse</p>
                  <p className="font-medium text-slate-900">{booking.address}</p>
                </div>
              </div>

              {/* Remarks */}
              {booking.remarks && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Remarques</p>
                  <p className="text-slate-900">{booking.remarks}</p>
                </div>
              )}
            </div>

            {/* Reference */}
            <div className="mt-8 p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500">Référence de réservation</p>
              <p className="font-mono text-sm text-slate-900 mt-1">{booking.id}</p>
            </div>

            {/* Email notice */}
            {booking.email && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <span>📧</span>
                  <span>Un email de confirmation a été envoyé à <strong>{booking.email}</strong>. Pensez à vérifier vos courriers indésirables (spam) si vous ne le recevez pas.</span>
                </p>
              </div>
            )}

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm text-blue-800">
                Besoin de modifier ou annuler ? Conservez ce lien :
              </p>
              <p className="font-mono text-xs text-blue-600 mt-1 break-all">
                {window.location.origin}/manage/{booking.id}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={() => navigate(`/manage/${booking.id}`)} variant="outline">
            Modifier / Annuler
          </Button>
          <Button onClick={() => navigate("/")} className="btn-primary">
            Nouvelle réservation
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ConfirmationPage;
